import { FORM_LIST_KEYWORD_FILTER } from '../../filters/config/forms';
import type {
	ColumnConfiguration,
	ListBuilderConfiguration,
	MobileCardConfiguration,
} from '../types';

/**
 * PD-16688 — the Forms list: Forms → Forms (portal-sites AllFormsPage) and the Forms
 * tab of a storefront and of All storefronts (FormsTab, which leaves out the
 * Storefront column). The rows are GET /api/forms shaped by formListQuery.ts
 * (FormListRowModel) and served in memory, so every field named here is one that
 * mapping produces. A row opens the form's editor through the page's onRowClick.
 */

const TABLE = 'form';

const COLUMNS: Array<ColumnConfiguration> = [
	{
		id: 'form',
		fields: [
			{
				id: 'form-name',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'name', objectTable: TABLE },
				},
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'text',
			},
			{
				id: 'form-break',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				// The key is what a website submits to, so it sits under the name.
				id: 'form-key',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'formKey', objectTable: TABLE },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h1', text: 'Form' }],
		minWidth: 220,
		sortable: true,
		alignment: 'left',
		sortFields: [{ direction: 'ASC', fieldName: 'name', objectTable: TABLE }],
		widthConfig: { mode: 'flexible', preset: 'large' },
		verticalAlignment: 'top',
	},
	{
		id: 'storefront',
		fields: [
			{
				id: 'storefront-name',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'storefrontName', objectTable: TABLE },
				},
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h1', text: 'Storefront' }],
		minWidth: 160,
		sortable: true,
		alignment: 'left',
		// The default order (storefront, then name, the shared forms last) rather than
		// the name alone, which would file "Every storefront" among the E's.
		sortFields: [
			{ direction: 'ASC', fieldName: 'storefrontOrder', objectTable: TABLE },
		],
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		id: 'kind',
		fields: [
			{
				id: 'kind-label',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'kindLabel', objectTable: TABLE },
				},
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h1', text: 'Kind' }],
		minWidth: 120,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{ direction: 'ASC', fieldName: 'kindLabel', objectTable: TABLE },
		],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'questions',
		fields: [
			{
				id: 'questions-count',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'fieldCount', objectTable: TABLE },
				},
				// Counts are whole numbers; the number style defaults to two decimals.
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
					formatOptions: { decimalPlaces: 0 },
				},
				displayStyle: 'number',
			},
		],
		headers: [{ id: 'h1', text: 'Questions' }],
		width: 120,
		sortable: true,
		alignment: 'right',
		sortFields: [
			{ direction: 'DESC', fieldName: 'fieldCount', objectTable: TABLE },
		],
		verticalAlignment: 'top',
	},
	{
		id: 'submissions',
		fields: [
			{
				id: 'submissions-count',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'submissionCount', objectTable: TABLE },
				},
				// Counts are whole numbers; the number style defaults to two decimals.
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
					formatOptions: { decimalPlaces: 0 },
				},
				displayStyle: 'number',
			},
		],
		headers: [{ id: 'h1', text: 'Submissions' }],
		width: 130,
		sortable: true,
		alignment: 'right',
		sortFields: [
			{ direction: 'DESC', fieldName: 'submissionCount', objectTable: TABLE },
		],
		verticalAlignment: 'top',
	},
	{
		id: 'status',
		fields: [
			{
				id: 'status-badge',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'isActive', objectTable: TABLE },
				},
				styleConfig: {
					size: 'sm',
					valueMapping: [
						{ value: true, label: 'On', color: { type: 'success' } },
						{ value: false, label: 'Off', color: { type: 'neutral' } },
					],
				},
				displayStyle: 'badge',
			},
		],
		headers: [{ id: 'h1', text: 'Status' }],
		width: 100,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{ direction: 'DESC', fieldName: 'isActive', objectTable: TABLE },
		],
		verticalAlignment: 'top',
	},
];

/**
 * Phone cards: the form (name over key) as the title, its submission count as the
 * figure on the right, storefront, kind and questions as chips, On/Off in the footer.
 */
const FORMS_MOBILE_CARD_CONFIG: MobileCardConfiguration = {
	cardLayout: 'zones',
	primaryFields: ['form'],
	heroField: 'submissions',
};

export const FORMS_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-forms-all',
			cacheTTL: 60000,
			name: 'All Forms',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: COLUMNS,
			mobileCardConfig: FORMS_MOBILE_CARD_CONFIG,
		},
	],
	// Everything on one page: the rows are already all in hand (formListQuery.ts) and
	// come back on a single page, so a smaller page size would only make the pager
	// read "1-25 of 30" over a list showing all 30 (the carts' reasoning, PD-13666).
	paging: {
		defaultPageSize: 1000,
		pageSizeOptions: [1000],
	},
	filters: {
		activeFilters: [FORM_LIST_KEYWORD_FILTER],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/forms/:id',
			objectLabel: 'Form',
			objectTable: TABLE,
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Form' },
				{ fieldName: 'formKey', fieldLabel: 'Key' },
				{ fieldName: 'storefrontName', fieldLabel: 'Storefront' },
				{ fieldName: 'kindLabel', fieldLabel: 'Kind' },
				{ fieldName: 'fieldCount', fieldLabel: 'Questions' },
				{ fieldName: 'submissionCount', fieldLabel: 'Submissions' },
				{ fieldName: 'isActive', fieldLabel: 'Status' },
			],
		},
	],
};
